import { EllipsisAccordion, FONT_SIZE } from "@dmm-com/rocket-web-ui-library";
import { useMemo } from "react";
import * as Styled from "./style";
import { isVideoLegacySeason, isVideoSeason, isVideoSpotLiveSeason } from "~/components/blocks/product/type-guard";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery } from "~/lib/graphql";

// TODO（kimura）: HTMLタグが問題なく表示されることを確認するための確認用のデータ。確認が完了したら消す
// const input = `<div class="note"> Some *emphasis* and <strong>strong</strong>! </div>`;
// const fontSizeTag = `<span style="font-size:40px">文字</span>`;
// const anchorLink = `<a href="/" style="color:red" >トップページに戻るリンクです</a>`;
// const imageTag = `<img src="https://placehold.jp/150x150.png" alt="画像説明">`;
// const htmlTagText = `<span style="font-size:16px"><b>「太字な」</b>岡田さんを<a href=/ style=color:#32CDFF>トップページに戻るリンク</a>に誘導します。この後、改行します。<br><span style="color:#FFF037">黄色な人間</span>が<i>「イタリック」</i>な文字を入力します。この後、改行します。<br>\n`;

export type DetailedInfoProps = {
  videoData: G_GetVideoQuery["video"];
};

/**
 * 詳細情報セクションを表示するコンポーネント
 * - 作品の詳細情報を表す文字列のデータが返却されます
 * - グラビア、舞台、VRなど既存納品経路のコンテンツの「詳細情報」のみ文字列ではなくHTMLタグが返却される予定
 * - 返却されるHTML文字列想定 : "<span style=\"font-size:40px\">文字</span>"
 * @param props {@link DetailedInfoProps}
 */
export const DetailedInfo = ({ videoData }: DetailedInfoProps) => {
  const { isSpSize } = useMatchMedia();
  const { content } = useQueryString();

  const textRenditionsArray = useMemo(() => {
    // 単数話 and エピソード末端 and 複数話 の場合
    if (isVideoSeason(videoData)) {
      if (content !== undefined) {
        // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
        if (!videoData.episode) return [];

        const {
          episode: {
            playInfo: { textRenditions },
          },
        } = videoData;

        return textRenditions;
      } else {
        const {
          episodes: { edges },
        } = videoData;

        if (edges.length === 0) return [];

        return edges[0].node.playInfo.textRenditions;
      }
    }

    // グラビアもしくはVR作品の場合
    if (isVideoLegacySeason(videoData)) {
      const {
        content: {
          playInfo: { textRenditions },
        },
      } = videoData;

      return textRenditions;
    }

    // スポット配信作品の場合
    if (isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return [];

      return edges[0].node.playInfo.textRenditions;
    }

    // TODO : 舞台作品の場合も実装する必要あり

    return [];
  }, [content, videoData]);

  const audioRenditionsArray = useMemo(() => {
    if (isVideoSeason(videoData)) {
      if (content) {
        // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
        if (!videoData.episode) return [];

        const {
          episode: {
            playInfo: { audioRenditions },
          },
        } = videoData;
        return audioRenditions;
      } else {
        const {
          episodes: { edges },
        } = videoData;

        if (edges.length === 0) return [];

        return edges[0].node.playInfo.audioRenditions;
      }
    }

    // グラビアもしくはVR作品の場合
    if (isVideoLegacySeason(videoData)) {
      const {
        content: {
          playInfo: { audioRenditions },
        },
      } = videoData;

      return audioRenditions;
    }

    // スポット配信作品の場合
    if (isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return [];

      return edges[0].node.playInfo.audioRenditions;
    }

    // TODO : 舞台作品の場合も実装する必要あり

    return [];
  }, [content, videoData]);

  const descriptionHTMLText = useMemo(() => {
    const notices =
      videoData.notices !== undefined && videoData.notices !== null ? `\n${videoData.notices.join("")}` : "";

    if (isVideoSeason(videoData)) {
      if (content !== undefined) {
        // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
        if (!videoData.episode) return "";
        return notices !== "" ? `${videoData.episode.episodeDetail}${notices}` : videoData.episode.episodeDetail;
      }
    }

    return notices !== "" ? `${videoData.description}${notices}` : videoData.description;
  }, [videoData]);

  return (
    <section>
      <Styled.Heading>詳細情報</Styled.Heading>

      <EllipsisAccordion
        line={3}
        text={<Styled.DivHTMLWrapper dangerouslySetInnerHTML={{ __html: descriptionHTMLText }} />}
        label="detailed-info"
        fontSize={!isSpSize ? FONT_SIZE.M : FONT_SIZE.S}
        lineHeight={1.6}
      />

      <Styled.DivGridContainer>
        {0 < textRenditionsArray.length && <span>字幕：{textRenditionsArray.join("、 ")}</span>}
        {0 < audioRenditionsArray.length && <span>音声：{audioRenditionsArray.join("、 ")}</span>}
      </Styled.DivGridContainer>
    </section>
  );
};
